"use client";

/**
 * The latest blog posts with their covers: the public homepage's strip,
 * and the signed-in hub's last section (owner, 2026-10-05: "always
 * visible at the bottom, with their images, like on the homepage").
 * The posts come from the server-side page wrapper (build-time catalogue).
 */
import BlogCoverImage from "@/components/blog/BlogCoverImage";
import Link from "@/lib/localized-link";
import { useLocale, useT } from "@/lib/locale-context";
import { formatPublishedAt, categoryLabel } from "@/lib/blog-format";

// Plain serializable data (no async functions, no Date instances) so
// the RSC -> client boundary can pass it through as JSON.
export interface HomepageBlogPost {
  slug: string;
  locale: string;
  title: string;
  summary: string;
  publishedAt: string;
  category: "news" | "guides" | "market";
  /** Resolved absolute path to the cover image (already prefixed for
   *  serving from /public). null when the post doesn't define one;
   *  the card falls back to a brand-coloured solid block. */
  coverUrl: string | null;
}

export default function HomeBlogSection({ posts, wrap = true }: {
  posts: HomepageBlogPost[];
  /** The public page wraps it in its own wide container; the hub places
   *  it inside its grid column. */
  wrap?: boolean;
}) {
  const t = useT();
  const { locale } = useLocale();
  if (posts.length === 0) return null;
  const body = (
    <>
      <div className={`flex items-end justify-between ${wrap ? "mb-6 sm:mb-8" : "mb-3"}`}>
        <h2 className={wrap ? "text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl md:text-4xl" : "text-lg font-bold tracking-tight text-gray-900"}>
          {t("home.blog.title")}
        </h2>
        <Link href="/blog" className={`shrink-0 font-semibold text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900 ${wrap ? "text-sm sm:text-base" : "text-sm"}`}>
          {t("home.blog.cta")} →
        </Link>
      </div>
      <ul className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${wrap ? "sm:gap-5" : ""}`}>
        {posts.map((p) => (
          <li key={p.slug} className="overflow-hidden rounded-2xl border border-gray-200 bg-white transition-colors hover:border-gray-300">
            <Link href={`/blog/${p.slug}`} className="flex h-full flex-col">
              {/* 16:9 cover; posts without one get the brand block so the
                  row stays level. Per-viewport WebP variants (BlogCoverImage). */}
              <div className="relative flex aspect-video w-full items-center justify-center bg-gray-100">
                {p.coverUrl ? (
                  <BlogCoverImage src={p.coverUrl} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
                ) : (
                  <span className="text-sm font-semibold tracking-wide text-white/80">Mənzil.ai</span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">{categoryLabel(p.category, locale)}</span>
                  <span>·</span>
                  <time dateTime={p.publishedAt}>{formatPublishedAt(p.publishedAt, locale)}</time>
                </div>
                <h3 className="mb-2 text-base font-bold leading-snug text-gray-900 sm:text-lg">{p.title}</h3>
                <p className="line-clamp-3 flex-1 text-sm leading-relaxed text-gray-600">{p.summary}</p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
  return wrap
    ? <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">{body}</section>
    : <section aria-label={t("home.blog.title")}>{body}</section>;
}
